import { Dayjs } from 'dayjs';
import { DayOfWeek } from '../../generated/gql-types';
import { parseScheduleDateLocal } from '../../util/jobScheduleBounds.util';

const DAYS_IN_WEEK = 7;
const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;
/** Shorter runs read better as a list ("Sat, Sun") than as a range. */
const MINIMUM_DAYS_FOR_RANGE_LABEL = 3;
/** Working-day picker is only offered when the schedule spans at least this many inclusive calendar days. */
const MINIMUM_INCLUSIVE_DAYS_FOR_WORKING_DAYS_SELECTION = 3;

/** Monday-first week order, matching the day pickers on web and mobile. */
export const ORDERED_DAYS_OF_WEEK: DayOfWeek[] = [
  DayOfWeek.Monday,
  DayOfWeek.Tuesday,
  DayOfWeek.Wednesday,
  DayOfWeek.Thursday,
  DayOfWeek.Friday,
  DayOfWeek.Saturday,
  DayOfWeek.Sunday,
];

const DAY_OF_WEEK_BY_DAYJS_INDEX: Record<number, DayOfWeek> = {
  0: DayOfWeek.Sunday,
  1: DayOfWeek.Monday,
  2: DayOfWeek.Tuesday,
  3: DayOfWeek.Wednesday,
  4: DayOfWeek.Thursday,
  5: DayOfWeek.Friday,
  6: DayOfWeek.Saturday,
};

export const WEEKDAYS_MON_FRI: DayOfWeek[] = ORDERED_DAYS_OF_WEEK.slice(0, 5);

/** Exact Mon–Fri selection with no weekend days. */
export const isMonToFriOnly = (days: DayOfWeek[]): boolean =>
  days.length === WEEKDAYS_MON_FRI.length &&
  WEEKDAYS_MON_FRI.every((day) => days.includes(day));

/** dayjs `.day()` index (0 = Sunday … 6 = Saturday). */
export const dayOfWeekToDayjsIndex = (day: DayOfWeek): number => {
  for (const [indexString, dayOfWeek] of Object.entries(
    DAY_OF_WEEK_BY_DAYJS_INDEX,
  )) {
    if (dayOfWeek === day) {
      return Number(indexString);
    }
  }
  return 0;
};

const SHORT_LABEL_BY_DAY_OF_WEEK: Record<DayOfWeek, string> = {
  [DayOfWeek.Monday]: 'Mon',
  [DayOfWeek.Tuesday]: 'Tue',
  [DayOfWeek.Wednesday]: 'Wed',
  [DayOfWeek.Thursday]: 'Thu',
  [DayOfWeek.Friday]: 'Fri',
  [DayOfWeek.Saturday]: 'Sat',
  [DayOfWeek.Sunday]: 'Sun',
};

const MINIMAL_LABEL_BY_DAY_OF_WEEK: Record<DayOfWeek, string> = {
  [DayOfWeek.Monday]: 'Mo',
  [DayOfWeek.Tuesday]: 'Tu',
  [DayOfWeek.Wednesday]: 'We',
  [DayOfWeek.Thursday]: 'Th',
  [DayOfWeek.Friday]: 'Fr',
  [DayOfWeek.Saturday]: 'Sa',
  [DayOfWeek.Sunday]: 'Su',
};

/** Single-letter badges on job cards (Tue and Thu both read as "T"). */
const INITIAL_LABEL_BY_DAY_OF_WEEK: Record<DayOfWeek, string> = {
  [DayOfWeek.Monday]: 'M',
  [DayOfWeek.Tuesday]: 'T',
  [DayOfWeek.Wednesday]: 'W',
  [DayOfWeek.Thursday]: 'T',
  [DayOfWeek.Friday]: 'F',
  [DayOfWeek.Saturday]: 'S',
  [DayOfWeek.Sunday]: 'S',
};

export const getDayOfWeekForDate = (date: Dayjs): DayOfWeek =>
  DAY_OF_WEEK_BY_DAYJS_INDEX[date.day()];

/** "Mon" — for summaries and schedule labels. */
export const getDayOfWeekShortLabel = (day: DayOfWeek): string =>
  SHORT_LABEL_BY_DAY_OF_WEEK[day];

/** "Mo" — for compact day toggles. */
export const getDayOfWeekMinimalLabel = (day: DayOfWeek): string =>
  MINIMAL_LABEL_BY_DAY_OF_WEEK[day];

/** "M" — for read-only working-day badges on job cards. */
export const getDayOfWeekInitialLabel = (day: DayOfWeek): string =>
  INITIAL_LABEL_BY_DAY_OF_WEEK[day];

/**
 * Which weekdays a job spans, in week order — aligned with create-job logic in
 * backend `Job.ts` (iterate calendar days; if the span is a week or longer, the
 * job covers every day of the week).
 *
 * @param startDateTime First day of the job schedule.
 * @param endDateTime Inclusive last day of the job schedule.
 */
export const getJobDaysForDateRange = (
  startDateTime: Dayjs,
  endDateTime: Dayjs,
): DayOfWeek[] => {
  const deltaMs = endDateTime.valueOf() - startDateTime.valueOf();
  const daysDelta = Math.floor(deltaMs / MILLISECONDS_PER_DAY);

  if (daysDelta >= DAYS_IN_WEEK) {
    return [...ORDERED_DAYS_OF_WEEK];
  }

  const daysInRange = new Set<DayOfWeek>();
  let current = startDateTime.startOf('day');
  const endDay = endDateTime.startOf('day');

  while (current.valueOf() <= endDay.valueOf()) {
    daysInRange.add(getDayOfWeekForDate(current));
    current = current.add(1, 'day');
  }

  // An end before the start covers no days at all — fall back to the start day.
  if (daysInRange.size === 0) {
    return [getDayOfWeekForDate(startDateTime)];
  }

  return ORDERED_DAYS_OF_WEEK.filter((day) => daysInRange.has(day));
};

/**
 * Inclusive calendar days between the schedule start and end (local midnights).
 * Mon–Mon → 1, Mon–Wed → 3, Mon–Sun → 7.
 */
export const getInclusiveScheduleDayCount = (
  startDateTime: Dayjs,
  endDateTime: Dayjs,
): number => {
  const startDay = startDateTime.startOf('day');
  const endDay = endDateTime.startOf('day');
  if (endDay.isBefore(startDay)) return 0;
  return endDay.diff(startDay, 'day') + 1;
};

/**
 * Days shown for working-day UI (picker or read-only badges), in week order:
 * - fewer than 3 inclusive calendar days → none (hidden)
 * - 3–6 days → only the weekdays the range covers
 * - 7 or more days → the full Mon–Sun week
 */
export const getVisibleWorkingDays = (
  startDateTime: Dayjs,
  endDateTime: Dayjs,
): DayOfWeek[] => {
  if (
    getInclusiveScheduleDayCount(startDateTime, endDateTime) <
    MINIMUM_INCLUSIVE_DAYS_FOR_WORKING_DAYS_SELECTION
  ) {
    return [];
  }

  return getJobDaysForDateRange(startDateTime, endDateTime);
};

/**
 * Form-facing wrapper around {@link getVisibleWorkingDays} for `YYYY-MM-DD` / `Date` inputs.
 *
 * @param startDate First day of the schedule, as held by the form (`YYYY-MM-DD` or `Date`).
 * @param endDate Inclusive last day of the schedule. Defaults to the start day.
 */
export const getSelectableWorkingDays = (
  startDate: string | Date | null | undefined,
  endDate: string | Date | null | undefined,
): DayOfWeek[] => {
  if (!startDate) return [];

  const scheduleStart = parseScheduleDateLocal(startDate);
  const scheduleEnd = endDate ? parseScheduleDateLocal(endDate) : scheduleStart;

  return getVisibleWorkingDays(scheduleStart, scheduleEnd);
};

/**
 * Selected working days stored on a job, limited to the visible set.
 * Falls back to every visible day when the job has no explicit `days.days`
 * (legacy / server-derived schedules).
 *
 * Unselected days in the visible set are intentionally omitted from this list —
 * they still render on the card, just muted.
 */
export const getJobSelectedWorkingDays = (
  job:
    | {
        days?: { days?: Array<DayOfWeek | null> | null } | null;
      }
    | null
    | undefined,
  visibleDays: DayOfWeek[],
): DayOfWeek[] => {
  const rawDays = job?.days?.days;
  if (rawDays == null || rawDays.length === 0) {
    return visibleDays;
  }

  const storedDays = rawDays.filter((day): day is DayOfWeek => day != null);
  return visibleDays.filter((day) => storedDays.includes(day));
};

type JobWithWorkingDays =
  | {
      days?: { days?: Array<DayOfWeek | null> | null } | null;
    }
  | null
  | undefined;

/**
 * Whether a job actually runs on a given calendar day, honouring excluded
 * working days. Short schedules (under 3 inclusive days) still run on every
 * calendar day in the span.
 */
export const isJobScheduledOnCalendarDay = (
  job: JobWithWorkingDays,
  calendarDay: Dayjs,
  startDateTime: Dayjs,
  endDateTime: Dayjs,
): boolean => {
  const day = calendarDay.startOf('day');
  const startDay = startDateTime.startOf('day');
  const endDay = endDateTime.startOf('day');
  if (day.isBefore(startDay) || day.isAfter(endDay)) {
    return false;
  }

  const daysInSchedule = getJobDaysForDateRange(startDateTime, endDateTime);
  const weekday = getDayOfWeekForDate(day);

  if (
    getInclusiveScheduleDayCount(startDateTime, endDateTime) <
    MINIMUM_INCLUSIVE_DAYS_FOR_WORKING_DAYS_SELECTION
  ) {
    return daysInSchedule.includes(weekday);
  }

  const selectedDays = getJobSelectedWorkingDays(job, daysInSchedule);
  return selectedDays.includes(weekday);
};

export type WorkingDayBadge = {
  day: DayOfWeek;
  isSelected: boolean;
};

/**
 * Badges for job cards / details. Always Mon–Sun when the schedule is long
 * enough to show working days (≥ 3 inclusive calendar days). Days the job does
 * not run on (outside the span, or deselected) are still shown, muted.
 */
export const getWorkingDaysBadgesForJob = (
  job:
    | {
        days?: { days?: Array<DayOfWeek | null> | null } | null;
      }
    | null
    | undefined,
  startDateTime: Dayjs,
  endDateTime: Dayjs,
): WorkingDayBadge[] => {
  if (
    getInclusiveScheduleDayCount(startDateTime, endDateTime) <
    MINIMUM_INCLUSIVE_DAYS_FOR_WORKING_DAYS_SELECTION
  ) {
    return [];
  }

  const daysInSchedule = getJobDaysForDateRange(startDateTime, endDateTime);
  const selectedDays = getJobSelectedWorkingDays(job, daysInSchedule);

  return ORDERED_DAYS_OF_WEEK.map((day) => ({
    day,
    isSelected: selectedDays.includes(day),
  }));
};

/**
 * Adds or removes a working day, keeping the selection in week order.
 * The last selected day cannot be removed — a job must run on at least one day.
 *
 * @param selectedDays Days the job currently runs on.
 * @param dayToToggle Day the user tapped.
 */
export const toggleJobWorkingDay = (
  selectedDays: DayOfWeek[],
  dayToToggle: DayOfWeek,
): DayOfWeek[] => {
  const isSelected = selectedDays.includes(dayToToggle);

  if (isSelected && selectedDays.length <= 1) {
    return selectedDays;
  }

  const nextSelection = isSelected
    ? selectedDays.filter((day) => day !== dayToToggle)
    : [...selectedDays, dayToToggle];

  return ORDERED_DAYS_OF_WEEK.filter((day) => nextSelection.includes(day));
};

const areDaysConsecutive = (
  earlierDay: DayOfWeek,
  laterDay: DayOfWeek,
): boolean => {
  const earlierIndex = ORDERED_DAYS_OF_WEEK.indexOf(earlierDay);
  const laterIndex = ORDERED_DAYS_OF_WEEK.indexOf(laterDay);
  return (earlierIndex + 1) % DAYS_IN_WEEK === laterIndex;
};

const groupConsecutiveDays = (days: DayOfWeek[]): DayOfWeek[][] =>
  days.reduce<DayOfWeek[][]>((runs, day) => {
    const currentRun = runs[runs.length - 1];
    const previousDay = currentRun?.[currentRun.length - 1];

    if (previousDay && areDaysConsecutive(previousDay, day)) {
      currentRun.push(day);
      return runs;
    }

    return [...runs, [day]];
  }, []);

/**
 * "Mon – Fri, Sun" — consecutive days collapse into a range.
 *
 * @param days Days in week order.
 */
export const formatJobDaysSummary = (days: DayOfWeek[]): string =>
  groupConsecutiveDays(days)
    .map((run) => {
      if (run.length < MINIMUM_DAYS_FOR_RANGE_LABEL) {
        return run.map(getDayOfWeekShortLabel).join(', ');
      }
      const firstDayLabel = getDayOfWeekShortLabel(run[0]);
      const lastDayLabel = getDayOfWeekShortLabel(run[run.length - 1]);
      return `${firstDayLabel} – ${lastDayLabel}`;
    })
    .join(', ');
